import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Users, Repeat, MapPinLine } from "@phosphor-icons/react";

/**
 * RetentionTile — WAU/MAU stickiness signal for the admin top row.
 * Tells you what % of monthly visitors are also weekly visitors —
 * the canonical product-stickiness benchmark.
 *
 * Truck-finder intent subset (added Feb 2026): the share of MAU who
 * triggered a profile_view / pin_click / share_view event. Filters
 * out accidental visits, owner-dashboard-only sessions, and bots —
 * answers "of all these visitors, how many are here for the actual
 * intended purpose (finding trucks)?"
 *
 * Tier guidance (per industry rule of thumb):
 *   <10%  → discovery / one-shot tool
 *   10-20%  → typical content/marketplace product
 *   20-50%  → sticky utility (weekly habit)
 *   >50% → daily habit (Facebook/Slack territory)
 */
const TIER_COPY = {
  exceptional: { label: "Daily habit", color: "text-emerald-600 dark:text-emerald-300" },
  sticky: { label: "Sticky utility", color: "text-emerald-600 dark:text-emerald-300" },
  healthy: { label: "Healthy marketplace", color: "text-foreground" },
  discovery: { label: "Discovery-only", color: "text-amber-700 dark:text-amber-300" },
};

export default function RetentionTile() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let on = true;
    api
      .get("/admin/kpis/visitor-retention")
      .then((r) => on && setData(r.data || null))
      .catch(() => on && setData(null));
    return () => { on = false; };
  }, []);

  const ratio = data?.ratio_pct ?? null;
  const tier = data?.tier ? TIER_COPY[data.tier] : null;

  return (
    <div
      className="rounded-xl border border-border bg-card p-4"
      data-testid="admin-kpi-retention"
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
        <Repeat weight="duotone" className="h-3.5 w-3.5 text-primary" />
        Retention (WAU/MAU)
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="font-display font-bold text-2xl tabular-nums">
          {ratio == null ? "—" : `${ratio}%`}
        </span>
        {tier && (
          <span className={`text-[11px] font-semibold ${tier.color}`}>
            {tier.label}
          </span>
        )}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground flex items-center gap-1">
        <Users weight="duotone" className="h-3 w-3" />
        {data ? (
          <>
            <span className="tabular-nums">{(data.wau || 0).toLocaleString()}</span> WAU ·{" "}
            <span className="tabular-nums">{(data.mau || 0).toLocaleString()}</span> MAU
          </>
        ) : (
          "Loading…"
        )}
      </div>

      {/* Truck-finder intent block — answers "of these visitors, how
          many actually used the app to find a truck?" */}
      {data && data.mau_intent != null && (
        <div
          className="mt-2 pt-2 border-t border-border/60"
          data-testid="admin-kpi-retention-intent"
        >
          <div className="flex items-baseline gap-1.5">
            <MapPinLine weight="duotone" className="h-3.5 w-3.5 text-primary self-center" />
            <span className="font-display font-bold text-base tabular-nums">
              {data.intent_share_pct}%
            </span>
            <span className="text-[11px] font-semibold text-foreground">
              came to find trucks
            </span>
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
            {(data.wau_intent || 0).toLocaleString()} WAU ·{" "}
            {(data.mau_intent || 0).toLocaleString()} MAU
            {data.intent_ratio_pct != null && (
              <> · {data.intent_ratio_pct}% sticky among them</>
            )}
          </div>
        </div>
      )}

      <div className="mt-1.5 text-[10.5px] text-muted-foreground leading-tight">
        Weekly active ÷ monthly active. Intent = sessions that clicked a map pin, viewed a truck profile, or arrived via a shared truck link. Benchmarks: &lt;10% discovery, 10-20% marketplace, 20-50% sticky, &gt;50% daily habit.
      </div>
    </div>
  );
}
